import { useMemo } from 'react';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  type OnChangeFn,
  useReactTable,
} from '@tanstack/react-table';
import type { Item } from '@mas-flow/types';
import { useNavigate } from 'react-router-dom';
import { InlineScoreCell } from './InlineScoreCell';
import { InlineConfidenceCell } from './InlineConfidenceCell';
import { useCategories } from '../hooks/useCategories';
import type { Density } from '../hooks/useColumnPrefs';

export const ALL_COLUMNS = ['rank', 'title', 'category', 'bv', 'tc', 'rr', 'size', 'cod', 'wsjf', 'priorityBand', 'confidence', 'decision', 'lifecycle', 'owner', 'updated', 'anchorWhy', 'voters'] as const;
// anchorWhy and voters are opt-in: they live in ALL_COLUMNS (column picker + export support)
// but are excluded from the default visible set; the Anchors view adds anchorWhy back via
// TablePage's extraColumn prop.
export const DEFAULT_COLUMNS: readonly string[] = ALL_COLUMNS.filter((c) => c !== 'anchorWhy' && c !== 'voters');

export const HEADERS: Record<string, string> = {
  rank: '#', title: 'Item', category: 'Category', bv: 'BV', tc: 'TC', rr: 'RR', size: 'Size',
  cod: 'CoD', wsjf: 'WSJF', priorityBand: 'Band', confidence: 'Confidence',
  decision: 'Decision', lifecycle: 'Lifecycle', owner: 'Owner', updated: 'Updated',
  anchorWhy: 'Anchor reason',
  voters: 'Voters',
};

// Numeric columns share right alignment and tabular digits so values line up
// vertically and scan as a column of numbers.
const NUMERIC_COLS = new Set(['rank', 'bv', 'tc', 'rr', 'size', 'cod', 'wsjf', 'voters']);

const BAND_STYLES: Record<string, string> = {
  High: 'bg-emerald-50 text-emerald-700',
  Medium: 'bg-amber-50 text-amber-700',
  Low: 'bg-slate-100 text-slate-600',
};

function relativeTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return '';
  const diffMs = Date.now() - d;
  const days = Math.floor(diffMs / 86_400_000);
  if (days >= 1) return `${days}d ago`;
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours >= 1) return `${hours}h ago`;
  const mins = Math.floor(diffMs / 60_000);
  if (mins >= 1) return `${mins}m ago`;
  return 'just now';
}

const Empty = () => <span className="text-slate-300">—</span>;

export interface ItemTableProps {
  items: Item[];
  visibleCols: Set<string>;
  density: Density;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}

export function ItemTable({ items, visibleCols, density, sorting, onSortingChange }: ItemTableProps) {
  const navigate = useNavigate();
  const { data: categories } = useCategories();
  const categoryById = useMemo(
    () => new Map((categories ?? []).map((c) => [c.id, c])),
    [categories],
  );

  const columns = useMemo<ColumnDef<Item>[]>(() => {
    return ALL_COLUMNS.filter((c) => visibleCols.has(c)).map((id): ColumnDef<Item> => {
      switch (id) {
        case 'rank':
          return { id, header: HEADERS[id] ?? id, cell: ({ row }) => row.index + 1, enableSorting: false };
        case 'title':
          return {
            id,
            header: HEADERS[id] ?? id,
            accessorFn: (it) => it.title,
            cell: ({ row }) => (
              <span className="flex min-w-[14rem] max-w-[26rem] items-baseline gap-1.5">
                <span className="truncate font-medium text-slate-900" title={row.original.title}>
                  {row.original.title}
                </span>
                {row.original.isAnchor && <span className="shrink-0 text-amber-600" title="anchor">⚓</span>}
              </span>
            ),
          };
        case 'category':
          return {
            id,
            header: HEADERS[id] ?? id,
            accessorFn: (it) => categoryById.get(it.categoryId)?.name ?? it.categoryId,
            cell: ({ row }) => {
              const cat = categoryById.get(row.original.categoryId);
              return (
                <span className="flex items-center gap-1.5 whitespace-nowrap text-slate-600">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: cat?.color ?? '#cbd5e1' }}
                    aria-hidden
                  />
                  {cat?.name ?? row.original.categoryId}
                </span>
              );
            },
          };
        case 'bv': return { id, header: HEADERS[id] ?? id, accessorFn: (it) => it.scores.businessValue ?? -1, cell: ({ row }) => <InlineScoreCell item={row.original} field="businessValue" /> };
        case 'tc': return { id, header: HEADERS[id] ?? id, accessorFn: (it) => it.scores.timeCriticality ?? -1, cell: ({ row }) => <InlineScoreCell item={row.original} field="timeCriticality" /> };
        case 'rr': return { id, header: HEADERS[id] ?? id, accessorFn: (it) => it.scores.riskOpportunity ?? -1, cell: ({ row }) => <InlineScoreCell item={row.original} field="riskOpportunity" /> };
        case 'size': return { id, header: HEADERS[id] ?? id, accessorFn: (it) => it.scores.jobSize ?? -1, cell: ({ row }) => <InlineScoreCell item={row.original} field="jobSize" /> };
        case 'cod': return { id, header: HEADERS[id] ?? id, accessorFn: (it) => it.costOfDelay ?? -1, cell: ({ row }) => row.original.costOfDelay ?? <Empty /> };
        case 'wsjf': return {
          id,
          header: HEADERS[id] ?? id,
          accessorFn: (it) => it.wsjf ?? -1,
          cell: ({ row }) =>
            row.original.wsjf != null
              ? <span className="font-semibold text-slate-900">{row.original.wsjf.toFixed(1)}</span>
              : <Empty />,
        };
        case 'priorityBand': return {
          id,
          header: HEADERS[id] ?? id,
          accessorFn: (it) => it.priorityBandOverride ?? it.priorityBand ?? '',
          cell: ({ row }) => {
            const band = row.original.priorityBandOverride ?? row.original.priorityBand;
            if (!band) return <Empty />;
            return (
              <span className="whitespace-nowrap">
                <span className={`rounded px-1.5 py-0.5 text-xs ${BAND_STYLES[band] ?? 'bg-slate-100 text-slate-600'}`}>{band}</span>
                {row.original.priorityBandOverride && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-700">OVR</span>}
              </span>
            );
          },
        };
        case 'confidence': return { id, header: HEADERS[id] ?? id, accessorFn: (it) => it.confidence ?? '', cell: ({ row }) => <InlineConfidenceCell item={row.original} /> };
        case 'decision': return {
          id,
          header: HEADERS[id] ?? id,
          accessorFn: (it) => it.decision ?? '',
          cell: ({ row }) => row.original.decision ?? <span className="text-slate-400">Inbox</span>,
        };
        case 'lifecycle': return { id, header: HEADERS[id] ?? id, accessorFn: (it) => it.lifecycle };
        case 'owner': return {
          id,
          header: HEADERS[id] ?? id,
          accessorFn: (it) => it.owner?.displayName ?? '',
          cell: ({ row }) => (
            <span className="block max-w-[11rem] truncate text-slate-500" title={row.original.owner?.displayName ?? ''}>
              {row.original.owner?.displayName ?? <Empty />}
            </span>
          ),
        };
        case 'updated': return {
          id,
          header: HEADERS[id] ?? id,
          accessorFn: (it) => it.updatedAt,
          cell: ({ row }) => (
            <span className="whitespace-nowrap text-slate-500" title={row.original.updatedAt}>
              {relativeTime(row.original.updatedAt)}
            </span>
          ),
        };
        case 'anchorWhy':
          return {
            id,
            header: HEADERS[id] ?? id,
            accessorFn: (it) => it.anchorWhy ?? '',
            cell: ({ row }) => (
              <span className="block max-w-[20rem] truncate text-slate-700" title={row.original.anchorWhy ?? ''}>
                {row.original.anchorWhy ?? <Empty />}
              </span>
            ),
          };
        case 'voters':
          return {
            id,
            header: HEADERS[id] ?? id,
            accessorFn: (it) => Object.keys(it.votes ?? {}).length,
            cell: ({ row }) => Object.keys(row.original.votes ?? {}).length,
          };
        default: { const _exhaustive: never = id; return _exhaustive; }
      }
    });
  }, [visibleCols, categoryById]);

  const table = useReactTable({
    data: items,
    columns,
    state: { sorting },
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const cellPad = density === 'compact' ? 'px-2.5 py-1.5' : 'px-3 py-2.5';

  return (
    <table className="w-full border-collapse text-sm">
      <thead className="border-b border-slate-200 bg-slate-50">
        {table.getHeaderGroups().map((hg) => (
          <tr key={hg.id}>
            {hg.headers.map((h) => {
              const numeric = NUMERIC_COLS.has(h.column.id);
              const sorted = h.column.getIsSorted();
              return (
                <th
                  key={h.id}
                  onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined}
                  className={`whitespace-nowrap text-xs font-medium uppercase tracking-wide ${cellPad} ${
                    numeric ? 'text-right' : 'text-left'
                  } ${sorted ? 'text-slate-900' : 'text-slate-500'} ${h.column.getCanSort() ? 'cursor-pointer select-none hover:text-slate-900' : ''}`}
                >
                  {flexRender(h.column.columnDef.header, h.getContext())}
                  {sorted === 'asc' && <span className="ml-1">↑</span>}
                  {sorted === 'desc' && <span className="ml-1">↓</span>}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row, idx) => (
          <tr
            key={row.id}
            className="cursor-pointer border-b border-slate-100 transition-colors last:border-b-0 hover:bg-slate-50"
            onClick={() => navigate({ search: `?item=${row.original.id}` })}
          >
            {row.getVisibleCells().map((cell) => {
              const numeric = NUMERIC_COLS.has(cell.column.id);
              return (
                <td
                  key={cell.id}
                  className={`${cellPad} ${numeric ? 'text-right tabular-nums' : ''} ${
                    cell.column.id === 'rank' ? 'w-10 text-slate-400' : ''
                  }`}
                >
                  {cell.column.id === 'rank'
                    ? idx + 1
                    : flexRender(cell.column.columnDef.cell ?? String(cell.getValue() ?? ''), cell.getContext())}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
